import { Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { ConfirmationService } from 'primeng/api';
import { MessageModule } from 'primeng/message';
import { Tooltip } from 'primeng/tooltip';
import { MouldingFlowSummary } from '../../models/moulding-item.model';
import { MouldingItemService } from '../moulding-page/services/moulding-item.service';

@Component({
  selector: 'app-moulding-list-page',
  standalone: true,
  imports: [ButtonModule, SharedConfirmDialogComponent, MessageModule, Tooltip],
  providers: [ConfirmationService],
  templateUrl: './moulding-list-page.component.html',
})
export class MouldingListPageComponent implements OnInit {
  private readonly itemService = inject(MouldingItemService);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);

  readonly items = signal<MouldingFlowSummary[]>([]);
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  ngOnInit(): void {
    void this.loadItems();
  }

  async newItem(): Promise<void> {
    this.error.set(null);
    try {
      const item = await firstValueFrom(this.itemService.createItem$());
      void this.router.navigate(['/auto-molds', item.id]);
    } catch {
      this.error.set('Failed to create a new flow.');
    }
  }

  confirmDeleteItem(item: MouldingFlowSummary, event: Event): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${item.name}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        void this.deleteItem(item.id);
      },
    });
  }

  openFlow(id: number): void {
    void this.router.navigate(['/auto-molds', id]);
  }

  private async loadItems(): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const items = await firstValueFrom(this.itemService.getItems$());
      this.items.set(items);
    } catch {
      this.error.set('Failed to load flows.');
      this.items.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async deleteItem(id: number): Promise<void> {
    this.error.set(null);
    try {
      await firstValueFrom(this.itemService.deleteItem$(id));
      this.items.update((items) => items.filter((item) => item.id !== id));
    } catch {
      this.error.set('Failed to delete this flow.');
    }
  }
}
